import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	PLATFORM_ID,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ArcElement, Chart, ChartDataset, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(...registerables);
Chart.register(ChartDataLabels);

interface RingLayoutConfig {
	inner: number;
	outer: number;
}

type DoughnutDataset = ChartDataset<'doughnut', number[]> & { isTrack?: boolean };

interface LegendItem {
	label: string;
	value: number;
	color: string;
}

@Component({
	selector: 'app-radial-stacked-chart',
	templateUrl: './radial-stacked-chart.component.html',
	styleUrls: ['./radial-stacked-chart.component.scss']
})
export class RadialStackedChartComponent implements AfterViewInit, OnChanges, OnDestroy {
	private static idCounter = 0;
	private ringLayout: RingLayoutConfig[] = [];

	@ViewChild('chartCanvas') canvasRef?: ElementRef<HTMLCanvasElement>;

	private chart: Chart<'doughnut'> | null = null;
	private viewInitialized = false;
	private destroyed = false;
	private readonly generatedId = `radial-stacked-chart-${++RadialStackedChartComponent.idCounter}`;
	private readonly isBrowser: boolean;

	@Input() idChart?: string;
	@Input() labels: string[] = [];
	@Input() data: number[] = [];
	@Input() colors: string[] = ['#87CEEB', '#DA70D6'];
	@Input() trackColor = '#F2F2F2';
	@Input() cutout = 70;
	@Input() ringWidth = 12;
	@Input() ringSpacing = 4;

	legendItems: LegendItem[] = [];
	private palette: string[] = [];

	constructor(private readonly cdr: ChangeDetectorRef, @Inject(PLATFORM_ID) platformId: object) {
		this.isBrowser = isPlatformBrowser(platformId);
	}

	get canvasId(): string {
		return this.idChart || this.generatedId;
	}

	ngAfterViewInit(): void {
		if (!this.isBrowser) {
			return;
		}
		this.viewInitialized = true;
		this.renderChart();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!this.isBrowser) {
			return;
		}
		if (!this.viewInitialized) {
			return;
		}

		if (
			changes['data'] ||
			changes['labels'] ||
			changes['colors'] ||
			changes['trackColor'] ||
			changes['cutout'] ||
			changes['ringWidth'] ||
			changes['ringSpacing']
		) {
			this.renderChart();
		}
	}

	ngOnDestroy(): void {
		this.destroyed = true;
		this.destroyChart();
	}

	private renderChart(): void {
		if (!this.isBrowser) {
			return;
		}
		if (!Array.isArray(this.data) || !Array.isArray(this.labels) || !this.data.length || !this.labels.length) {
			this.ringLayout = [];
			this.scheduleLegendUpdate([]);
			this.destroyChart();
			return;
		}

		let context: CanvasRenderingContext2D | null = null;
		try {
			context = this.canvasRef?.nativeElement.getContext('2d') ?? null;
		} catch (error) {
			console.warn('Skipping radial stacked chart render (SSR/DOM not supported).', error);
			return;
		}
		if (!context) {
			console.error('Failed to acquire 2D context for radial stacked chart');
			return;
		}

		this.destroyChart();

		this.palette = this.buildPalette(this.colors);
		const datasets = this.buildDatasets();
		this.updateLegendItems();
		const radiusPlugin = {
			id: `${this.canvasId}-radius-plugin`,
			beforeDatasetsDraw: (chartInstance: Chart<'doughnut'>) => this.applyRingLayout(chartInstance)
		};

		try {
			this.chart = new Chart(context, {
				type: 'doughnut',
				data: {
					labels: [...this.labels],
					datasets
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					rotation: 0,
					cutout: `${this.resolveCutout()}%`,
					plugins: {
						datalabels: {
							display: false
						},
						legend: {
							display: false
						},
						tooltip: {
							filter: tooltipItem => {
								const dataset = tooltipItem.dataset as DoughnutDataset;
								if (dataset?.isTrack) {
									return false;
								}
								return tooltipItem.dataIndex === 0;
							},
							callbacks: {
								label: context => {
									const label = context.dataset.label || '';
									const value = Number(context.raw || 0).toFixed(1);
									return ` ${label}: ${value}%`;
								}
							}
						}
					}
				},
				plugins: [radiusPlugin]
			});
		} catch (error) {
			console.warn('Skipping radial stacked chart render (SSR/DOM not supported).', error);
			this.destroyChart();
		}
	}

	private applyRingLayout(chartInstance: Chart<'doughnut'>): void {
		if (!this.ringLayout.length || !chartInstance.chartArea) {
			return;
		}

		const { left, right, top, bottom } = chartInstance.chartArea;
		const chartWidth = Math.max(0, right - left);
		const chartHeight = Math.max(0, bottom - top);
		const maxRadius = Math.min(chartWidth, chartHeight) / 2;
		if (maxRadius <= 0) {
			return;
		}

		this.ringLayout.forEach((layout, datasetIndex) => {
			const meta = chartInstance.getDatasetMeta(datasetIndex);
			meta.data.forEach(arc => {
				const arcElement = arc as ArcElement;
				arcElement.outerRadius = (layout.outer / 100) * maxRadius;
				arcElement.innerRadius = (layout.inner / 100) * maxRadius;
			});
		});
	}

	private buildDatasets(): DoughnutDataset[] {
		const palette = this.palette?.length ? this.palette : this.buildPalette(this.colors);
		const baseCutout = this.resolveCutout();
		const { width: computedWidth, spacing: computedSpacing } = this.resolveRingMetrics(baseCutout);
		let currentOuterRadius = 100;
		const layouts: RingLayoutConfig[] = [];
		const datasets: DoughnutDataset[] = [];

		this.data.forEach((value, index) => {
			const percentage = this.normalizeValue(value);
			const remaining = Math.max(0, 100 - percentage);
			const color = palette[index];
			const innerRadius = Math.max(baseCutout, currentOuterRadius - computedWidth);
			const layout = { inner: innerRadius, outer: currentOuterRadius };

			const valueDataset: DoughnutDataset = {
				label: this.labels[index],
				data: [percentage, remaining],
				backgroundColor: [color, 'rgba(0,0,0,0)'],
				borderColor: [color, 'rgba(0,0,0,0)'],
				hoverBackgroundColor: [color, 'rgba(0,0,0,0)'],
				hoverBorderColor: [color, 'rgba(0,0,0,0)'],
				borderWidth: 0,
				spacing: 0,
				borderRadius: [20, 0],
				hoverOffset: 0
			};

			const trackDataset: DoughnutDataset = {
				label: `${this.labels[index] || 'track'}-track`,
				data: [100],
				backgroundColor: this.trackColor,
				borderColor: this.trackColor,
				hoverBackgroundColor: this.trackColor,
				hoverBorderColor: this.trackColor,
				borderWidth: 0,
				spacing: 0,
				hoverOffset: 0,
				isTrack: true
			};

			datasets.push(valueDataset, trackDataset);
			layouts.push(layout, layout);

			currentOuterRadius = Math.max(baseCutout, innerRadius - computedSpacing);
		});

		this.ringLayout = layouts;
		return datasets;
	}

	trackByLegend(index: number, item: LegendItem): string {
		return `${item.label}-${index}`;
	}

	private updateLegendItems(): void {
		if (!Array.isArray(this.labels) || !this.labels.length) {
			this.scheduleLegendUpdate([]);
			return;
		}

		const items = this.labels.map((label, index) => ({
			label,
			color: this.palette[index] || '#E0E0E0',
			value: this.normalizeValue(this.data[index] ?? 0)
		}));

		this.scheduleLegendUpdate(items);
	}

	private scheduleLegendUpdate(items: LegendItem[]): void {
		Promise.resolve().then(() => {
			if (this.destroyed) {
				return;
			}
			this.legendItems = items;
			this.cdr.markForCheck();
		});
	}

	private normalizeValue(value: number): number {
		if (!isFinite(value)) {
			return 0;
		}
		if (value < 0) {
			return 0;
		}
		if (value > 100) {
			return 100;
		}
		return Number(value);
	}

	private buildPalette(baseColors: string[]): string[] {
		const paletteSource = baseColors?.length ? baseColors : ['#87CEEB', '#DA70D6'];
		const palette = [...paletteSource];
		while (palette.length < this.data.length) {
			palette.push(...paletteSource);
		}

		return palette.slice(0, this.data.length);
	}

	private resolveCutout(): number {
		if (!isFinite(this.cutout)) {
			return 70;
		}

		return Math.min(90, Math.max(35, this.cutout));
	}

	private resolveRingMetrics(baseCutout: number): { width: number; spacing: number } {
		const width = this.normalizeRingWidth(this.ringWidth);
		const spacing = this.normalizeRingSpacing(this.ringSpacing);
		const totalRings = this.data.length;
		if (!totalRings) {
			return { width, spacing };
		}

		const availableSpace = Math.max(5, 100 - baseCutout);
		const requiredSpace = totalRings * width + Math.max(0, totalRings - 1) * spacing;

		if (requiredSpace <= availableSpace) {
			return { width, spacing };
		}

		const scale = availableSpace / requiredSpace;
		return { width: width * scale, spacing: spacing * scale };
	}

	private normalizeRingWidth(value: number): number {
		if (!isFinite(value)) {
			return 12;
		}

		return Math.min(25, Math.max(6, value));
	}

	private normalizeRingSpacing(value: number): number {
		if (!isFinite(value)) {
			return 4;
		}

		return Math.min(12, Math.max(2, value));
	}

	private destroyChart(): void {
		if (this.chart) {
			this.chart.destroy();
			this.chart = null;
		}
	}
}
